import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchInvoiceRequest, Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Card } from '../../../components/card/card';
import { Paginator } from '../../../components/paginator/paginator-v2';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { InvoiceList, DEFAULT_INVOICE_TABLE_COLUMNS } from '../../collectibles/invoice-list';
import { TenantContext } from '../../../context/tenant-context';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { Location } from '../../../clients/quipli-service/location/location';
import { SearchCommissionRequest } from '../../../clients/quipli-service/report/report';
import Icon from '../../../components/icons/icon';

export const CommissionInvoices = ({
  admin,
  commissionPeriod,
  invoiceIds,
  backToCommission,
}: {
  admin: Admin;
  commissionPeriod: DateTime;
  invoiceIds: string[];
  backToCommission: () => void;
}) => {
  const { searchInvoices } = useInvoiceApiV1();
  const { listLocations, getConfiguration } = useAPI();
  const { configuration } = useContext(TenantContext);
  const currency = configuration?.currency ?? 'USD';
  const [invoices, setInvoices] = useState<Pagination<Invoice>>();
  const [locations, setLocations] = useState<Location[]>();
  const [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>();

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<SearchInvoiceRequest & SearchCommissionRequest>(
    {
      sortBy: params.get('sortBy') || 'dueDate',
      sortDirection: params.get('sortDirection') || 'desc',
      pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
      pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
    } as SearchInvoiceRequest & SearchCommissionRequest,
    () => refreshInvoices(),
  );

  async function refreshInvoices(sortBy?: 'date' | 'dueDate' | 'invoiceNumber') {
    if (sortBy) {
      setRequest((req) => {
        let sortDirection: 'asc' | 'desc' = 'asc';
        if (req.sortBy === sortBy) {
          sortDirection = req.sortDirection === 'asc' ? 'desc' : 'asc';
        }
        return {
          ...req,
          sortBy: sortBy || req.sortBy,
          sortDirection,
          invoiceIds,
        };
      });
    } else if (request.pageSize) {
      await searchInvoices({ ...request, invoiceIds }).then((res) => setInvoices(res));
    }
  }

  useEffect(() => {
    listLocations().then(setLocations);
    getConfiguration('CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN').then((c) =>
      setDisplayTaxByJurisdiction(c?.value === '1'),
    );
  }, []);

  return (
    <div className="collectibles flex flex-column gap-4">
      <div className="flex gap-3 items-center">
        <Icon icon="Back" size="xlarge" onClick={() => backToCommission()} />
        <h2 className="my-0 py-0">
          Sales Commission Invoices - {admin.adminFullName} {commissionPeriod.toFormat('LLL yyyy')}
        </h2>
      </div>
      <Card>
        {invoices && currency && locations && displayTaxByJurisdiction !== undefined && (
          <InvoiceList
            invoices={invoices.items}
            refresh={refreshInvoices}
            locations={locations}
            currency={currency}
            sortable={true}
            enabledColumns={[...DEFAULT_INVOICE_TABLE_COLUMNS, 'DaysPastDue']}
            displayTaxByJurisdiction={displayTaxByJurisdiction}
          />
        )}
        <Paginator
          pagination={invoices?.pagination}
          request={request}
          setRequest={(pag) =>
            setRequest((req) => {
              return { ...req, ...pag };
            })
          }
        />
      </Card>
    </div>
  );
};
